// ══════════════════════════════════════════════════════════════════════════════════════════════
//  QUE EL NAVEGADOR NO META EL CORREO DEL INICIO DE SESION EN UN CAMPO DE BUSCAR
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  EL FALLO, con las palabras del dueño: «cuando se inicia sesion muchas veces sale el correo con
//  el que se inicia sesion pegado en el buscador del header o de agenda». No lo escribe nadie: lo
//  pega el navegador. Chrome mira cada <input> de texto y, cuando no encuentra con qué
//  identificarlo —sin etiqueta, sin `name`, sin `autocomplete`—, lo clasifica por parecido; un
//  campo suelto en una cabecera se le parece a «el correo del usuario», y ahí va el que guardó del
//  formulario de entrada. Los buscadores de pacientes lo tienen peor todavía, porque su propio
//  marcador de posición dice «nombre, teléfono o email».
//
//  POR QUÉ NO SE ARREGLA CON UN useEffect QUE LO BORRE. Porque el relleno ocurre DESPUÉS de pintar
//  y a veces después del efecto: se ve el correo, parpadea y vuelve. Se arregla en el sitio donde
//  el navegador toma la decisión, que es el MARCADO del campo.
//
//  LAS TRES PIEZAS, y hacen falta las tres:
//
//   1. `autoComplete="off"`. Es lo que dice la norma y lo respetan Firefox y Safari. Chrome lo
//      IGNORA en los campos que ha clasificado como de identidad, así que por sí solo no basta —y
//      por eso este fichero no se queda ahí.
//   2. Un `name` QUE NO SUENE A IDENTIDAD. Es la pieza que de verdad desarma la clasificación de
//      Chrome, que se hace leyendo `name`, `id`, la etiqueta y el marcador de posición. Todos los
//      campos de buscar de la casa pasan a llamarse `busq-…`, y de ese nombre se retiran además
//      las palabras que disparan al clasificador (correo, usuario, nombre, teléfono, DNI…).
//   3. Las marcas de los GESTORES DE CONTRASEÑAS (1Password, LastPass, Dashlane), que no miran
//      `autocomplete` sino sus propios atributos. Sin ellas el campo sigue saliendo con el icono
//      del llavero encima y su menú de cuentas.
//
//  LO QUE NO SE HACE, A PROPÓSITO: NO se pone `autoComplete="new-password"` —el truco clásico para
//  espantar el relleno—, porque entonces el navegador trata el buscador como un campo de
//  contraseña: ofrece guardar lo que se escribe y en algunos casos lo oculta. Un buscador tiene que
//  seguir siendo un campo de texto normal, y `contratoDeBusqueda` lo comprueba.
//
//  QUIÉN LO USA. `components/InputBusqueda.jsx` (la caja compartida, de la que cuelgan la mayoría
//  de los buscadores de la aplicación) y, uno a uno, los campos que llevan su propio `<input>`: la
//  cabecera del panel de administración, el paciente de una cita nueva en la agenda, el almacén, el
//  chat, marketing, recalls…
//
//  LA GUARDIA: `backend/routes/buscadores-sin-autorrelleno.test.js`. Monta las pantallas y le lee
//  los atributos al campo que pintan — un grep no valdría, porque casi ningún buscador escribe sus
//  atributos: los hereda.

// Las palabras por las que un navegador decide que un campo es de identidad. Se usa para DOS cosas:
// limpiarlas del nombre que se genera, y comprobar en la guardia que ningún buscador acabó con un
// `name` que las lleve.
export const PALABRAS_DE_IDENTIDAD = /(mail|correo|usuario|user|login|nombre|name|apellid|telefono|tel|phone|movil|nif|dni|passw|pass|clave|contrase|direccion|address|tarjeta|card)/i

const SIN_TILDES = (s) => String(s == null ? '' : s).normalize('NFD').replace(/[̀-ͯ]/g, '')

// Palabras que no distinguen nada: se caen para que el nombre quede corto y legible en el inspector
// del navegador («Buscar por nombre o email» → `busq-campo`, no `busq-buscar-por--o-`).
const RELLENO = new Set(['buscar', 'busca', 'busque', 'busqueda', 'escribe', 'escribir', 'elige',
  'empieza', 'el', 'la', 'los', 'las', 'un', 'una', 'unos', 'unas', 'de', 'del', 'al', 'a', 'en',
  'o', 'y', 'por', 'para', 'con', 'que', 'se', 'su', 'lo', 'aqui', 'cualquier'])

// `doctor` → `busq-doctor`; `nombre o email` → `busq-campo` (las palabras de identidad se caen).
// Nunca devuelve cadena vacía: un campo sin `name` es exactamente el caso que el navegador clasifica
// a ojo, que es lo que se está evitando.
export function nombreDeBusqueda(que) {
  const trozos = SIN_TILDES(que)
    .toLowerCase()
    .replace(new RegExp(PALABRAS_DE_IDENTIDAD.source, 'gi'), ' ')
    .split(/[^a-z0-9]+/)
    .filter((t) => t && !RELLENO.has(t))
  return 'busq-' + (trozos.slice(0, 3).join('-') || 'campo')
}

// El juego de atributos que hay que poner en TODO campo de buscar de la aplicación. Se esparce con
// {...antiAutorrelleno('lo-que-busca')} justo detrás de `<input`, o sea ANTES del resto de props,
// para que quien necesite otro `name` o su propio `autoComplete` pueda seguir poniéndolo encima.
export function antiAutorrelleno(que) {
  return {
    name: nombreDeBusqueda(que),
    autoComplete: 'off',
    'data-lpignore': 'true',      // LastPass
    'data-1p-ignore': 'true',     // 1Password
    'data-form-type': 'other',    // Dashlane
    'data-busqueda': 'si',        // la marca de la casa
    spellCheck: false,
  }
}

// ── EL CONTRATO, EN UN SOLO SITIO ─────────────────────────────────────────────────────────────
// Lo usa la guardia sobre las pantallas MONTADAS: por cada campo con pinta de buscador comprueba
// que cumple esto. Vive aquí y no en la prueba para que el día que cambie el remedio (otro atributo,
// otro prefijo) cambie en UN sitio y la guardia siga midiendo lo mismo. Devuelve la lista de fallos:
// vacía = el campo está bien.
export function contratoDeBusqueda(props) {
  const fallos = []
  const p = props || {}
  const ac = p.autoComplete
  if (ac == null || ac === '') fallos.push('sin autoComplete: el navegador lo rellena a su gusto')
  else if (/^(on|email|username|tel|name)$/i.test(String(ac))) fallos.push('autoComplete="' + ac + '" INVITA al relleno')
  else if (/password/i.test(String(ac))) fallos.push('autoComplete="' + ac + '": el navegador lo trata como campo de contraseña')
  if (p.type != null && /^(email|password|tel|url)$/i.test(String(p.type))) fallos.push('type="' + p.type + '": no es un campo de identidad, es un buscador')
  const n = p.name
  if (n == null || String(n) === '') fallos.push('sin name: es lo que Chrome usa para clasificarlo, y sin el lo adivina')
  else if (PALABRAS_DE_IDENTIDAD.test(String(n))) fallos.push('name="' + n + '" suena a campo de identidad')
  return fallos
}
